import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Heart, ArrowUpRight, Globe, HandHeart, ArrowLeft } from "lucide-react";

// PayPal.me handle owned by the app maintainer
const PAYPAL_URL = "https://paypal.me/bradharris1281";
const KAFUNJO_URL = "https://www.kafunjoprojectus.org/take-action";

export default function Support() {
  const { user } = useAuth();

  return (
    <div className="grain-bg min-h-screen relative overflow-hidden" data-testid="support-page">
      {/* Soft ambient blobs */}
      <div className="absolute -top-40 -right-24 w-[520px] h-[520px] rounded-full opacity-40 blur-3xl" style={{ background: "radial-gradient(circle, #D4C7E1 0%, transparent 60%)" }} />
      <div className="absolute -bottom-40 -left-24 w-[520px] h-[520px] rounded-full opacity-30 blur-3xl" style={{ background: "radial-gradient(circle, #EFEBF4 0%, transparent 60%)" }} />

      {/* Lightweight header */}
      <header className="relative z-10 border-b border-[#EFEBF4]/60">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="support-brand" className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ background: "#9B8BAF" }}>
              <Heart className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
            </div>
            <span className="text-base tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              My Daily Prayer
            </span>
          </Link>
          <Link
            to={user ? "/dashboard" : "/"}
            data-testid="support-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-[#75737A] hover:text-[#2C2A2E] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {user ? "Back to journal" : "Back to home"}
          </Link>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        {/* Intro */}
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.22em] bg-[#EFEBF4] text-[#75737A] border border-[#D4C7E1]">
            <HandHeart className="h-3 w-3" /> Support &amp; Give
          </div>
          <h1
            className="mt-6 text-5xl sm:text-6xl leading-[1.05] text-[#2C2A2E]"
            data-testid="support-title"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Prayer moves us
            <br />
            <span className="italic text-[#9B8BAF]">to give.</span>
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-[#75737A]">
            My Daily Prayer is a quiet gift, freely offered. If it has blessed your
            walk with the Lord, consider supporting the journal or a mission close
            to our heart.
          </p>
          <p
            className="mt-5 text-sm text-[#9B8BAF] italic"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            &ldquo;Each of you should give what you have decided in your heart to
            give, not reluctantly or under compulsion, for God loves a cheerful
            giver.&rdquo;
            <span className="not-italic text-[#75737A] text-xs tracking-wide">
              {" "}
              — 2 Corinthians 9:7
            </span>
          </p>
        </div>

        {/* Cards */}
        <section className="mt-14 grid md:grid-cols-2 gap-6 md:gap-7">
          <SupportCard
            testId="card-mdp"
            eyebrow="For the journal"
            title="Support My Daily Prayer"
            body="Help us keep this journal quiet, ad-free, and available to everyone. Your gift covers hosting, email delivery, and time spent tending it."
            verse={{ text: "Freely you have received; freely give.", ref: "— Matthew 10:8" }}
            cta="Give with PayPal"
            href={PAYPAL_URL}
            accent="#9B8BAF"
            accentSoft="#EFEBF4"
            iconBg="#D4C7E1"
            Icon={Heart}
          />
          <SupportCard
            testId="card-kafunjo"
            eyebrow="For the mission field"
            title="Kafunjo Project USA"
            body="Partner with children and families in Uganda through Kafunjo Project USA — a ministry close to our heart, serving with education, care, and gospel hope."
            verse={{ text: "Religion that God accepts is this: to look after orphans and widows in their distress.", ref: "— James 1:27" }}
            cta="Take action at kafunjoprojectus.org"
            href={KAFUNJO_URL}
            external
            accent="#8FA396"
            accentSoft="#EEF3EE"
            iconBg="#CFDDD1"
            Icon={Globe}
          />
        </section>

        {/* Footer note */}
        <div className="mt-16 pt-8 border-t border-[#EFEBF4] flex flex-col md:flex-row gap-3 md:items-center md:justify-between text-xs text-[#75737A]">
          <span>My Daily Prayer is a private, ad-free journal. Any gift is a kindness — never required.</span>
          <Link to="/" className="text-[#9B8BAF] hover:text-[#8A799E] underline underline-offset-4">
            Return home
          </Link>
        </div>
      </main>
    </div>
  );
}

function SupportCard({ testId, eyebrow, title, body, verse, cta, href, external, accent, accentSoft, iconBg, Icon }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : "_self"}
      rel={external ? "noopener noreferrer" : undefined}
      data-testid={testId}
      className="group relative flex flex-col rounded-2xl bg-white border border-[#D4C7E1]/70 p-8 lg:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(90,70,120,0.35)]"
    >
      <div className="flex items-center justify-between">
        <div
          className="h-11 w-11 rounded-full flex items-center justify-center"
          style={{ background: iconBg }}
        >
          <Icon className="h-4 w-4 text-white" strokeWidth={2.5} />
        </div>
        <span
          className="text-[10px] uppercase tracking-[0.22em]"
          style={{ color: accent }}
        >
          {eyebrow}
        </span>
      </div>
      <h2
        className="mt-6 text-3xl text-[#2C2A2E] leading-tight"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        {title}
      </h2>
      <p className="mt-4 text-[15px] leading-relaxed text-[#3F3D42]">{body}</p>
      <div
        className="mt-5 rounded-xl px-4 py-3 text-xs italic leading-relaxed"
        style={{ background: accentSoft, fontFamily: "'Playfair Display', serif" }}
      >
        <span className="text-[#3F3D42]">&ldquo;{verse.text}&rdquo;</span>
        <span className="block not-italic text-[#75737A] text-[11px] tracking-wide mt-1">{verse.ref}</span>
      </div>
      <div className="mt-8 flex items-center justify-between">
        <span
          className="inline-flex items-center gap-1.5 text-sm font-medium transition-transform group-hover:translate-x-0.5"
          style={{ color: accent }}
        >
          {cta}
          <ArrowUpRight className="h-4 w-4" />
        </span>
      </div>
    </a>
  );
}
